/**
 * MyEquipmentPage — standalone "Mi equipación" section
 * (DASHBOARD-SIDEMENU-PROFILES-1-FIX2 · Feb 2026).
 *
 * Player-only · uses the same `localStorage` store as `Mi QR` and
 * `Mis encordados` for the player's rackets list.
 */
import React, { useEffect, useState } from "react";
import PanelLayout from "@/components/layout/PanelLayout";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Package, ShieldCheck } from "lucide-react";
import {
  EquipmentEditor,
  loadRackets,
  saveRackets,
  T as RACKET_LAB_T,
} from "@/components/RacketLabPlayerPreview";

const COPY = {
  es: { eyebrow: "Racket Lab", badge: "Preview controlado", title: "Mi equipación" },
  en: { eyebrow: "Racket Lab", badge: "Controlled preview", title: "My gear" },
};

export default function MyEquipmentPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const tx = RACKET_LAB_T[lang] || RACKET_LAB_T.en;
  const copy = COPY[lang] || COPY.en;
  const isPlayer = user?.profile_key === "player";

  const [rackets, setRackets] = useState(loadRackets);
  useEffect(() => { saveRackets(rackets); }, [rackets]);

  if (user && !isPlayer) return <Navigate to="/app" replace />;

  return (
    <PanelLayout>
      <div data-testid="page-my-equipment">
        <header className="flex flex-wrap items-center gap-3 mb-2">
          <Package className="w-6 h-6 text-emerald-700" />
          <div className="tsw-overline">{copy.eyebrow}</div>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold uppercase tracking-wider"
            data-testid="page-my-equipment-badge"
          >
            <ShieldCheck className="w-3 h-3" />{copy.badge}
          </span>
        </header>
        <h1
          className="text-2xl font-bold text-slate-900 mb-6"
          data-testid="page-my-equipment-title"
        >
          {copy.title}
        </h1>

        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6">
          <EquipmentEditor tx={tx} rackets={rackets} setRackets={setRackets} />
        </div>

        <p className="text-[11px] text-slate-500 mt-4 italic">{tx.legalBlurb}</p>
      </div>
    </PanelLayout>
  );
}
